import { useEffect, useState } from "react";
import { adminApi, eur, fmtDay } from "@/lib/adminApi";

const Stat = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl border border-line bg-surface2 p-4" data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-widest text-muted2">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${accent || ""}`}>{value}</p>
  </div>
);

const GrowthChart = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState([]);
  useEffect(() => { adminApi.get(`/admin/growth?days=${days}`).then((r) => setData(r.data.days || [])).catch(() => {}); }, [days]);
  const maxDev = Math.max(1, ...data.map((d) => d.new_devices));
  const maxRev = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div className="rounded-2xl border border-line bg-surface2 p-5" data-testid="growth-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold">Rast</h3>
        <div className="flex gap-1 rounded-full border border-line p-1">
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`growth-tab-${d}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${days === d ? "bg-brand text-white" : "text-txt2 hover:text-white"}`}>{d} dana</button>
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-4 text-[11px] text-muted2">
        <span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-sm bg-brand" />Novi uređaji</span>
        <span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-sm bg-ok" />Zarada (€)</span>
      </div>
      <div className="mt-4 flex h-44 items-end gap-1 overflow-x-auto">
        {data.map((d) => (
          <div key={d.date} className="group flex min-w-[14px] flex-1 flex-col items-center gap-1" title={`${fmtDay(d.date)} • ${d.new_devices} novih • ${eur(d.revenue)}`}>
            <div className="flex h-36 w-full items-end justify-center gap-[2px]">
              <div className="w-1/2 rounded-t bg-brand transition-all" style={{ height: `${(d.new_devices / maxDev) * 100}%`, minHeight: d.new_devices ? 3 : 0 }} />
              <div className="w-1/2 rounded-t bg-ok transition-all" style={{ height: `${(d.revenue / maxRev) * 100}%`, minHeight: d.revenue ? 3 : 0 }} />
            </div>
            <span className="text-[9px] text-muted2">{fmtDay(d.date)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Overview = ({ refreshKey }) => {
  const [s, setS] = useState(null);
  const [w, setW] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
    adminApi.get("/admin/stats/weekly").then((r) => setW(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!s) return <p className="text-muted2" data-testid="overview-loading">Učitavanje…</p>;
  return (
    <div className="flex flex-col gap-4" data-testid="overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Ukupno" value={s.total} testid="stat-total" />
        <Stat label="Aktivni" value={s.active} accent="text-emerald-400" testid="stat-active" />
        <Stat label="Proba" value={s.trial} accent="text-yellow-400" testid="stat-trial" />
        <Stat label="Istekli" value={s.expired} accent="text-red-400" testid="stat-expired" />
        <Stat label="Godišnji" value={s.yearly} testid="stat-yearly" />
        <Stat label="Doživotni" value={s.lifetime} testid="stat-lifetime" />
        <Stat label="Novi danas" value={s.new_today} testid="stat-new-today" />
        <Stat label="Uplate" value={s.paid_count} testid="stat-paid-count" />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <Stat label="Zarada danas" value={eur(s.revenue_today)} accent="text-brand" testid="stat-rev-today" />
        <Stat label="Ovaj mesec" value={eur(s.revenue_month)} accent="text-brand" testid="stat-rev-month" />
        <Stat label="Ukupno zarada" value={eur(s.revenue_total)} accent="text-brand" testid="stat-rev-total" />
        <div className="rounded-2xl border border-brand/40 bg-brandTert p-4" data-testid="stat-weekly">
          <p className="text-[11px] font-bold uppercase tracking-widest text-onBrandTert">Ova nedelja</p>
          <p className="mt-1 text-2xl font-bold">{eur(w?.revenue_eur)}</p>
          <p className="text-xs text-txt2">{w?.new_customers ?? 0} novih kupaca</p>
        </div>
      </div>
      <GrowthChart />
    </div>
  );
};
